import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Upload, CheckCircle, XCircle, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';

interface ImportResult {
  success: number;
  failed: number;
  errors: string[];
}

const CarImport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { currentLineId, currentTenantId } = useAuth();
  const { canManage, isReady: rolesReady } = useSiteRoles(currentLineId || undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  usePermissionGuard({
    enabled: rolesReady && !!currentLineId,
    allowed: canManage,
    message: t('dashboard.permissionAccessDenied'),
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
    }
  };

  const parseCSV = (content: string): Array<Record<string, string>> => {
    const lines = content.split('\n').filter(line => line.trim());
    
    // Skip first line (encoding info), second line is headers
    const headers = lines[1].split(';').map(h => h.trim());
    const data: Array<Record<string, string>> = [];

    // Parse data starting from line 3 (index 2)
    for (let i = 2; i < lines.length; i++) {
      const values = lines[i].split(';');
      if (values.length === headers.length) {
        const row: Record<string, string> = {};
        headers.forEach((header, index) => {
          row[header] = values[index]?.trim() || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast({
        title: t('carImport.noFile'),
        description: t('carImport.selectFile'),
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    const errors: string[] = [];
    let successCount = 0;
    let failCount = 0;

    try {
      const content = await selectedFile.text();
      const data = parseCSV(content);

      for (const row of data) {
        // Use Kennummer as VIN (unique identifier), Model, and Barva (color)
        const vin = row.Kennummer || '';
        const model = row.Model || '';
        const color = row.Barva || '';

        if (!vin || vin === '-' || vin.includes('-    -')) {
          failCount++;
          errors.push(`${t('carImport.skipRow')}: ${model} - ${t('carImport.noVin')}`);
          continue;
        }

        if (!currentTenantId || !currentLineId) {
          failCount++;
          errors.push(t('carImport.noSiteSelected'));
          continue;
        }

        // Check if car already exists
        const { data: existing } = await ((supabase as any)
          .schema('mod_styling'))
          .from('cars')
          .select('vin')
          .eq('vin', vin)
          .eq('tenant_id', currentTenantId)
          .eq('line_id', currentLineId)
          .maybeSingle();

        if (existing) {
          failCount++;
          errors.push(`${t('carImport.duplicate')}: ${vin}`);
          continue;
        }

        // Insert car
        const { error } = await ((supabase as any)
          .schema('mod_styling'))
          .from('cars')
          .insert({
            tenant_id: currentTenantId,
            line_id: currentLineId,
            vin,
            model,
            color,
            status: 'pending',
          });

        if (error) {
          failCount++;
          errors.push(`${vin}: ${error.message}`);
        } else {
          successCount++;
        }
      }

      setResult({
        success: successCount,
        failed: failCount,
        errors: errors.slice(0, 10), // Show max 10 errors
      });

      if (successCount > 0) {
        toast({
          title: t('carImport.success'),
          description: t('carImport.imported', { count: successCount }),
        });
      }
    } catch (error) {
      toast({
        title: t('carImport.error'),
        description: error instanceof Error ? error.message : t('carImport.unknownError'),
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted">
      <header className="bg-card border-b border-border shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('carImport.backToManager')}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t('carImport.title')}</h1>
            <p className="text-muted-foreground">{t('carImport.description')}</p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{t('carImport.uploadFile')}</CardTitle>
              <CardDescription>{t('carImport.uploadDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">
                    <FileText className="h-4 w-4" />
                    {t('carImport.chooseFile')}
                  </div>
                  <input
                    id="csv-upload"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
                {selectedFile && (
                  <span className="text-sm text-muted-foreground">{selectedFile.name}</span>
                )}
              </div>

              <Button
                onClick={handleImport}
                disabled={!selectedFile || isProcessing}
                className="w-full"
              >
                <Upload className="mr-2 h-4 w-4" />
                {isProcessing ? t('carImport.processing') : t('carImport.import')}
              </Button>
            </CardContent>
          </Card>

          {result && (
            <Card>
              <CardHeader>
                <CardTitle>{t('carImport.results')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2 p-4 bg-success/10 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-success" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('carImport.successful')}</p>
                      <p className="text-2xl font-bold">{result.success}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-4 bg-destructive/10 rounded-lg">
                    <XCircle className="h-5 w-5 text-destructive" />
                    <div>
                      <p className="text-sm text-muted-foreground">{t('carImport.failed')}</p>
                      <p className="text-2xl font-bold">{result.failed}</p>
                    </div>
                  </div>
                </div>

                {result.errors.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">{t('carImport.errors')}</h3>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {result.errors.map((error, index) => (
                        <p key={index} className="text-sm text-destructive">
                          {error}
                        </p>
                      ))}
                      {result.failed > result.errors.length && (
                        <p className="text-sm text-muted-foreground">
                          {t('carImport.moreErrors', { count: result.failed - result.errors.length })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default CarImport;
